import axios from 'axios'
import { message } from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEnvironment } from 'hooks/useEnvironment'
import _ from 'lodash'
import qs from 'qs'

// for save cookies and send in each requests
axios.defaults.withCredentials = true

// get api base url
const getApiBaseUrl = () => {
  return `https://${process.env.REACT_APP_RD_APP_DOMAIN_API}/api/v1/administration`
}

// get auth base url for all requests
const getAuthBaseUrl = () => {
  return `${getApiBaseUrl()}/auth`
}

// get app base url for all requests
const getAppBaseUrl = () => {
  return `${getApiBaseUrl()}/overall`
}

// serialize data to send for requests
const getURLSearchParamsFromObject = (obj) => {
  return qs.stringify(obj, { encode: true })
}

export const useRequestsEvents = () => {
  // for redirect
  const navigate = useNavigate()

  const { profile } = useParams()

  // for translation
  const { t } = useTranslation('notifications')

  // get environment events
  const {
    getCurrentLocale,
    getAuthUserToken,
    setAuthUserTokens,
    getAuthUserRefreshToken,
    deleteAuthUserToken
  } = useEnvironment()

  // get header for all request
  const getHeaders = (cfg = {}) => {
    return {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept-Language': getCurrentLocale(),
        Authorization: `Bearer ${getAuthUserToken()}`,
        'X-Tenant-Profile': profile,
        ...cfg
      }
    }
  }

  // on start requests
  const onStartRequest = async (caller) => {
    // get global mask before request
    const globalMask = document.getElementById('global_mask')

    // if exist global mask
    if (globalMask) {
      globalMask.style.visibility = 'visible'
    }

    // if caller include progress...
    if (!caller.includes(':progress')) {
      return
    }

    // fix antd warning
    const title = t(
      'notification.in_progress',
      {
        ns: 'notifications'
      }
    )

    // show loading message with 0 (not dismiss)
    message.loading(
      title,
      0
    )
  }

  // on finish requests
  const onFinishRequest = async (caller) => {
    // get global mask before request
    const globalMask = document.getElementById('global_mask')

    // if exist global mask
    if (globalMask) {
      globalMask.style.visibility = 'hidden'
    }

    // if exists windows...
    if (!caller.includes(':progress')) {
      return
    }

    // hide current message not close if login, more action use the same message
    message.destroy()
  }

  // parse failure requests
  const parseFailureRequests = (
    error,
    callBack = () => {},
    caller = undefined
  ) => {
    // call to common actions on finish request
    onFinishRequest(caller).then(async () => {
      // check errors
      if (
        error.response &&
        error.response.status
      ) {
        callBack(
          error.response.status,
          error.response
        )

        // check if caller has a custom errors form api
        if (
          caller.includes(':custom_errors') &&
          _.has(
            error,
            'response.data.errors'
          )
        ) {
          // get config for error to show...
          const errorConfig =
            error.response.data.errors[0]

          // if error has message to show, show a message
          if (
            _.has(
              errorConfig,
              'msg'
            )
          ) {
            return message.error(
              errorConfig.msg
            )
          }
        }

        if (
          error.response.status === 400
        ) {
          return message.error(
            t(
              'notification.failure.bad_request',
              {
                ns: 'notifications'
              }
            )
          )
        } else if (
          error.response.status === 401
        ) {
          if (
            !caller.includes(':no_401_msg')
          ) {
            message.warning(
              t(
                'notification.failure.no_session',
                {
                  ns: 'notifications'
                }
              )
            )
          }

          return deleteAuthUserToken()
        } else if (
          error.response.status === 429
        ) {
          return message.warning(
            t(
              'notification.failure.max_requests',
              {
                ns: 'notifications'
              }
            )
          )
        } else if (
          error.response.status === 404
        ) {
          return message.warning(
            t(
              'notification.failure.not_found',
              {
                ns: 'notifications'
              }
            )
          )
        } else if (
          error.response.status === 403
        ) {
          return message.warning(
            t(
              'notification.failure.forbidden',
              {
                ns: 'notifications'
              }
            )
          )
        }
      }

      // for catch a 500 error if caller is a form...
      if (
        caller.includes(':form') ||
        caller.includes(':table')
      ) {
        return message.error(
          t(
            'notification.failure.unknown',
            {
              ns: 'notifications'
            }
          )
        )
      }

      return navigate(
        '/500',
        {
          state: 500,
          status: 500
        }
      )
    })
  }

  // parse success requests
  const parseSuccessRequests = (
    response,
    callBack = () => {},
    caller = undefined,
    file = false
  ) => {
    // call to common actions on finish request
    onFinishRequest(caller).then(() => {
      // if call is successfully
      if (
        response.data.success ||
        (
          file &&
          response.status === 200
        )
      ) {
        if (
          (
            caller.includes(':form') ||
            caller.includes(':table')
          ) &&
          !caller.includes(':no_success_msg')
        ) {
          message.success(
            t(
              'notification.success.form',
              {
                ns: 'notifications'
              }
            )
          )
        }

        callBack(
          response.data,
          response
        )
      } else if (
        caller !==
          'authentication@getAuthNewToken' &&
        caller.includes(':progress')
      ) {
        message.error(
          t(
            'notification.failure.unknown',
            {
              ns: 'notifications'
            }
          )
        )
      }
    })
  }

  // METHODS -> AUTH
  const getAuthNewToken = (
    successCallback = () => {},
    errorCallback = () => {},
    msg = 'no_401_msg'
  ) => {
    const name =
      `authentication@getAuthNewToken:${msg}`

    onStartRequest(name).then(() => {
      axios.post(
        `${getAuthBaseUrl()}/user_refresh_token`,
        getURLSearchParamsFromObject({
          token_refresh:
            getAuthUserRefreshToken()
        }),
        getHeaders()
      ).then(function (response) {
        setAuthUserTokens({
          ...response.data,
          user_refresh_token:
            getAuthUserRefreshToken()
        })

        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const authUser = (
    formData,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      'authentication@authuser:progress:no_401_msg'

    onStartRequest(name).then(() => {
      axios.post(
        `${getAuthBaseUrl()}/user_login`,
        getURLSearchParamsFromObject(
          formData
        ),
        getHeaders()
      ).then(function (response) {
        setAuthUserTokens(
          response.data
        )

        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getUserProfiles = (
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      'authentication@authuser:getUserProfiles'

    onStartRequest(name).then(() => {
      axios.get(
        `${getAuthBaseUrl()}/user_profiles/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getUserPermissionsByProfile = (
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      'authentication@authuser:getUserPermissionsByProfile'

    onStartRequest(name).then(() => {
      axios.get(
        `${getAuthBaseUrl()}/user_profiles/permissions/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  // APPLICATIONS TYPES
  const getApplicationTypes = (
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      'applicationsTypes@getApplicationTypes'

    onStartRequest(name).then(() => {
      axios.get(
        `${getApiBaseUrl()}/applications/types/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  // METHODS MEASUREMENTS UNITS
  const getMeasurementsUnits = (
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      'measurementsUnits@getMeasurementsUnits'

    onStartRequest(name).then(() => {
      axios.get(
        `${getApiBaseUrl()}/measurements/units/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  // METHODS SUPPLY COMPANIES
  const getSupplyCompanies = (
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      'supplyCompanies@getSupplyCompanies'

    onStartRequest(name).then(() => {
      axios.get(
        `${getApiBaseUrl()}/supply/companies/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  // METHODS RESIDENTIAL
  const getResidentialUsers = (
    successCallback = () => {},
    errorCallback = () => {},
    limit = 30,
    offset = 0
  ) => {
    const name =
      'residential@getResidentialUsers'

    onStartRequest(name).then(() => {
      axios.get(
        `${getApiBaseUrl()}/residential/users/index?limit=${limit}&offset=${offset}`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  // PROMOTIONS :: INDEX
  const getPromotions = (
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      'promotions@getPromotions'

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  // PROMOTIONS :: PROMOTION DATA
  const getPromotion = (
    promotionId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}@getPromotion`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/index`,
        getHeaders()
      ).then(function (response) {
        _.set(
          window,
          `promotion-${promotionId}.promotion_permissions`,
          _.get(
            response,
            'data.data.promotion_permissions',
            {}
          )
        )

        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  // PROMOTIONS :: BLOCKS
  const getPromotionBlocks = (
    promotionId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}@getPromotionBlocks`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/blocks/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  // PROMOTIONS :: APARTMENTS
  const getPromotionApartments = (
    promotionId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}@getPromotionApartments`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/apartments/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  // PROMOTIONS :: RESIDENTIAL USERS
  const getPromotionResidentialUsers = (
    promotionId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}@getPromotionResidentialUsers`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/residential/users/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  // PROMOTIONS :: CREATE / ASSOCIATE RESIDENTIAL USER
  const createPromotionResidentialUser = (
    promotionId,
    payload,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}@createPromotionResidentialUser:form:progress`

    onStartRequest(name).then(() => {
      axios.post(
        `${getAppBaseUrl()}/promotions/${promotionId}/residential/users/create`,
        getURLSearchParamsFromObject(
          payload
        ),
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  // PROMOTIONS :: UPDATE RESIDENTIAL USER RELATION
  const updatePromotionResidentialUser = (
    promotionId,
    relationId,
    payload,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${relationId}@updatePromotionResidentialUser:form:progress`

    onStartRequest(name).then(() => {
      axios.put(
        `${getAppBaseUrl()}/promotions/${promotionId}/residential/users/${relationId}/update`,
        getURLSearchParamsFromObject(
          payload
        ),
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  // PROMOTIONS :: DELETE RESIDENTIAL USER RELATION
  const deletePromotionResidentialUser = (
    promotionId,
    relationId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${relationId}@deletePromotionResidentialUser:table:progress`

    onStartRequest(name).then(() => {
      axios.delete(
        `${getAppBaseUrl()}/promotions/${promotionId}/residential/users/${relationId}/delete`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  // PROMOTIONS :: RESIDENTIAL USER APARTMENTS
  const getPromotionResidentialUserApartments = (
    promotionId,
    relationId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${relationId}@getPromotionResidentialUserApartments`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/residential/users/${relationId}/apartments/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  // PROMOTIONS :: ASSIGN APARTMENT TO RESIDENTIAL USER
  const createPromotionResidentialUserApartment = (
    promotionId,
    relationId,
    payload,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${relationId}@createPromotionResidentialUserApartment:form:progress`

    onStartRequest(name).then(() => {
      axios.post(
        `${getAppBaseUrl()}/promotions/${promotionId}/residential/users/${relationId}/apartments/create`,
        getURLSearchParamsFromObject(
          payload
        ),
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  // PROMOTIONS :: UPDATE RESIDENTIAL USER APARTMENT
  const updatePromotionResidentialUserApartment = (
    promotionId,
    relationId,
    apartmentId,
    payload,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${relationId}-${apartmentId}@updatePromotionResidentialUserApartment:form:progress`

    onStartRequest(name).then(() => {
      axios.put(
        `${getAppBaseUrl()}/promotions/${promotionId}/residential/users/${relationId}/apartments/${apartmentId}/update`,
        getURLSearchParamsFromObject(
          payload
        ),
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  // PROMOTIONS :: DELETE RESIDENTIAL USER APARTMENT
  const deletePromotionResidentialUserApartment = (
    promotionId,
    relationId,
    apartmentId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${relationId}-${apartmentId}@deletePromotionResidentialUserApartment:table:progress`

    onStartRequest(name).then(() => {
      axios.delete(
        `${getAppBaseUrl()}/promotions/${promotionId}/residential/users/${relationId}/apartments/${apartmentId}/delete`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionHolder = (
    promotionId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}@getPromotionHolder`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/person/holder/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionBlockHolder = (
    promotionId,
    blockId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${blockId}@getPromotionBlockHolder`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/block/${blockId}/holder/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionBlockPresident = (
    promotionId,
    blockId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${blockId}@getPromotionBlockPresident`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/block/${blockId}/president/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionBlockAdministrator = (
    promotionId,
    blockId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${blockId}@getPromotionBlockAdministrator`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/block/${blockId}/administrator/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionPresident = (
    promotionId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}@getPromotionPresident`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/person/president/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionAdministrator = (
    promotionId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}@getPromotionAdministrator`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/person/administrator/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionContact = (
    promotionId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}@getPromotionContact`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/person/client/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionEnergySource = (
    promotionId,
    sourceId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}@getPromotionEnergySource`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/energy/source/${sourceId}/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionEnergySources = (
    promotionId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}@getPromotionEnergySources`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/energy/sources/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionEnergySourcesWithSupplyInvoices = (
    promotionId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}@getPromotionEnergySourcesWithSupplyInvoices`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/energy/sources/index?e_supply_invoices`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionEnergySourcesWithApplications = (
    promotionId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}@getPromotionEnergySourcesWithApplications`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/energy/sources/index?e_source_application&e_source_application_pipes&e_source_application_pipes_sensors`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionEnergySourcesWithLiquidatedPeriods = (
    promotionId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}@getPromotionEnergySourcesWithLiquidations`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/energy/sources/index?e_source_liquidated_periods`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionEnergySourcesWithLiquidationPeriods = (
    promotionId,
    period,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}@getPromotionEnergySourcesWithLiquidationPeriods`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/energy/sources/index?e_source_liquidation_periods&period=${period}`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionEnergySupplyInvoice = (
    promotionId,
    invoiceId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${invoiceId}@getPromotionEnergySupplyInvoice:no_success_msg`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/supply/invoice/${invoiceId}/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionEnergySourceApplication = (
    promotionId,
    sourceId,
    applicationId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${sourceId}-${applicationId}@getPromotionEnergySourceApplication:no_success_msg`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/energy/source/${sourceId}/applications/${applicationId}/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionsApplicationsPipesSensors = (
    promotionId,
    formData,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}@getPromotionsApplicationsPipesSensors:no_success_msg`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/applications/pipes/sensors/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionApplicationPipeSensorReadings = (
    promotionId,
    applicationId,
    pipeId,
    sensorId,
    successCallback = () => {},
    errorCallback = () => {},
    bodyData = null,
    queryData = {},
    limit = 30,
    offset = 0
  ) => {
    const name =
      `promotion-${promotionId}-${applicationId}-${pipeId}-${sensorId}@getPromotionApplicationPipeSensorReadings:no_success_msg`

    onStartRequest(name).then(() => {
      axios.post(
        `${getAppBaseUrl()}/promotions/${promotionId}/application/${applicationId}/pipe/${pipeId}/sensor/${sensorId}/readings/searches?limit=${limit}&offset=${offset}`,
        bodyData,
        {
          ...getHeaders(),
          params: queryData
        }
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionEnergySourceSupplyInvoices = (
    promotionId,
    sourceId,
    successCallback = () => {},
    errorCallback = () => {},
    limit = 5,
    offset = 0
  ) => {
    const name =
      `promotion-${promotionId}-${sourceId}@getPromotionEnergySourceSupplyInvoices:table:no_success_msg`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/energy/source/${sourceId}/supply/invoices/index?limit=${limit}&offset=${offset}`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionEnergySourceLiquidatedPeriods = (
    promotionId,
    sourceId,
    successCallback = () => {},
    errorCallback = () => {},
    limit = 30,
    offset = 0
  ) => {
    const name =
      `promotion-${promotionId}-${sourceId}@getPromotionEnergySourceLiquidatedPeriods:table:no_success_msg`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/energy/source/${sourceId}/liquidation/index?limit=${limit}&offset=${offset}`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionEnergySourceLiquidationCompensablePeriods = (
    promotionId,
    sourceId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${sourceId}@getPromotionEnergySourceLiquidatedPeriods:table:no_success_msg`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/energy/source/${sourceId}/liquidation/compensable/now/index`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionEnergySourceLiquidationPendingCompensationPeriods = (
    promotionId,
    sourceId,
    successCallback = () => {},
    errorCallback = () => {},
    limit = 30,
    offset = 0
  ) => {
    const name =
      `promotion-${promotionId}-${sourceId}@getPromotionEnergySourceLiquidationPendingCompensationPeriods:table:no_success_msg`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/energy/source/${sourceId}/liquidation/compensable/pending/index?limit=${limit}&offset=${offset}`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionEnergySourceLiquidationSummary = (
    promotionId,
    sourceId,
    start,
    end,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${sourceId}@getPromotionEnergySourceLiquidationSummary:table:no_success_msg`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/energy/source/${sourceId}/liquidation/summary/index?start=${start.format('YYYYMMDD')}&end=${end.format('YYYYMMDD')}`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const getPromotionBlockFloorsWithApartments = (
    promotionId,
    blockId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${blockId}@getPromotionBlockFloorsWithApartments`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/block/${blockId}/floors/index?apartments&persons`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const createPromotionEnergySupplyInvoice = (
    promotionId,
    formData,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}@createPromotionEnergySupplyInvoice:form:progress`

    onStartRequest(name).then(() => {
      axios.post(
        `${getAppBaseUrl()}/promotions/${promotionId}/supply/invoice/create`,
        {
          data:
            JSON.stringify(
              formData
            )
        },
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const updatePromotionEnergySource = (
    promotionId,
    sourceId,
    payload,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${sourceId}@updatePromotionEnergySource:form:progress`

    onStartRequest(name).then(() => {
      axios.put(
        `${getAppBaseUrl()}/promotions/${promotionId}/energy/source/${sourceId}/update`,
        {
          data:
            JSON.stringify(
              payload
            )
        },
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const updatePromotionEnergySourceApplication = (
    promotionId,
    sourceId,
    applicationId,
    payload,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${sourceId}-${applicationId}@updatePromotionEnergySourceApplication:form:progress`

    onStartRequest(name).then(() => {
      axios.put(
        `${getAppBaseUrl()}/promotions/${promotionId}/energy/source/${sourceId}/applications/${applicationId}/update`,
        {
          data:
            JSON.stringify(
              payload
            )
        },
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const updatePromotionEnergySupplyInvoice = (
    promotionId,
    invoiceId,
    payload,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${invoiceId}@updatePromotionEnergySupplyInvoice:form:progress`

    onStartRequest(name).then(() => {
      axios.put(
        `${getAppBaseUrl()}/promotions/${promotionId}/supply/invoice/${invoiceId}/update`,
        {
          data:
            JSON.stringify(
              payload
            )
        },
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const deletePromotionEnergySupplyInvoice = (
    promotionId,
    invoiceId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${invoiceId}@deletePromotionEnergySupplyInvoice:table:progress`

    onStartRequest(name).then(() => {
      axios.delete(
        `${getAppBaseUrl()}/promotions/${promotionId}/supply/invoice/${invoiceId}/delete`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const createEnergySourceLiquidatedPeriod = (
    promotionId,
    sourceId,
    payload,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${sourceId}@createEnergySourceLiquidatedPeriod:table:progress:custom_errors`

    onStartRequest(name).then(() => {
      axios.post(
        `${getAppBaseUrl()}/promotions/${promotionId}/energy/source/${sourceId}/liquidation/create`,
        {
          data:
            JSON.stringify(
              payload
            )
        },
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const deleteEnergySourceLiquidatedPeriod = (
    promotionId,
    sourceId,
    liquidationId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${sourceId}-${liquidationId}@deleteEnergySourceLiquidatedPeriod:table:progress:custom_errors`

    onStartRequest(name).then(() => {
      axios.delete(
        `${getAppBaseUrl()}/promotions/${promotionId}/energy/source/${sourceId}/liquidation/${liquidationId}/delete`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const downloadEnergySourceLiquidatedPeriod = (
    promotionId,
    sourceId,
    liquidationId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${sourceId}-${liquidationId}@downloadEnergySourceLiquidatedPeriod:table:progress`

    onStartRequest(name).then(() => {
      axios.get(
        `${getAppBaseUrl()}/promotions/${promotionId}/energy/source/${sourceId}/liquidation/${liquidationId}/download`,
        {
          ...getHeaders(),
          responseType: 'blob'
        }
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name,
          true
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name,
          true
        )
      })
    })
  }

  const updatePromotionEnergySupplyLiquidationPeriodicity = (
    promotionId,
    sourceId,
    payload,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}-${sourceId}@updatePromotionEnergySupplyLiquidationPeriodicity:table:progress:custom_errors`

    onStartRequest(name).then(() => {
      axios.put(
        `${getAppBaseUrl()}/promotions/${promotionId}/energy/source/${sourceId}/periodicity/update`,
        {
          data:
            JSON.stringify(
              payload
            )
        },
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  // PROMOTION :: UPLOAD IMAGE
  const uploadPromotionImage = async (
    promotionId,
    file,
    successCallback = () => {},
    errorCallback = () => {},
    onProgress = () => {}
  ) => {
    const name =
      `promotion-${promotionId}@uploadPromotionImage:progress:custom_errors`

    const fmData =
      new FormData()

    fmData.append(
      'image',
      file
    )

    onStartRequest(name).then(() => {
      axios.post(
        `${getAppBaseUrl()}/promotions/${promotionId}/image`,
        fmData,
        {
          ...getHeaders({
            'content-type':
              'multipart/form-data'
          }),

          onUploadProgress:
            event => {
              if (!event.total) {
                return
              }

              const percent =
                Math.floor(
                  (
                    event.loaded /
                    event.total
                  ) *
                  100
                )

              onProgress({
                percent
              })
            }
        }
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  // PROMOTION :: DELETE IMAGE
  const deletePromotionImage = (
    promotionId,
    successCallback = () => {},
    errorCallback = () => {}
  ) => {
    const name =
      `promotion-${promotionId}@deletePromotionImage:custom_errors`

    onStartRequest(name).then(() => {
      axios.delete(
        `${getAppBaseUrl()}/promotions/${promotionId}/image`,
        getHeaders()
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  const uploadFile = async (
    file,
    successCallback = () => {},
    errorCallback = () => {},
    onProgress = () => {},
    handlerUpdateProgress = () => {}
  ) => {
    const name =
      'dashboard@uploadFile:progress:custom_errors'

    const fmData =
      new FormData()

    fmData.append(
      'json',
      file
    )

    onStartRequest(name).then(() => {
      axios.post(
        `${getAppBaseUrl()}/upload/json`,
        fmData,
        {
          ...getHeaders({
            'content-type':
              'multipart/form-data'
          }),

          onUploadProgress:
            event => {
              const percent =
                Math.floor(
                  (
                    event.loaded /
                    event.total
                  ) *
                  100
                )

              handlerUpdateProgress(
                file,
                percent
              )

              onProgress({
                percent:
                  (
                    event.loaded /
                    event.total
                  ) *
                  100
              })
            }
        }
      ).then(function (response) {
        parseSuccessRequests(
          response,
          successCallback,
          name
        )
      }).catch(function (error) {
        parseFailureRequests(
          error,
          errorCallback,
          name
        )
      })
    })
  }

  return {
    getAuthNewToken,
    authUser,
    getUserProfiles,
    getUserPermissionsByProfile,

    getPromotion,
    getPromotions,
    getPromotionBlocks,

    getPromotionApartments,

    getPromotionResidentialUsers,
    createPromotionResidentialUser,
    updatePromotionResidentialUser,
    deletePromotionResidentialUser,

    getPromotionResidentialUserApartments,
    createPromotionResidentialUserApartment,
    updatePromotionResidentialUserApartment,
    deletePromotionResidentialUserApartment,

    getPromotionHolder,
    getPromotionPresident,
    getPromotionContact,
    getPromotionAdministrator,

    getPromotionEnergySource,
    getPromotionEnergySources,
    getPromotionEnergySupplyInvoice,
    getPromotionEnergySourceApplication,
    getPromotionEnergySourceSupplyInvoices,
    getPromotionEnergySourceLiquidatedPeriods,
    getPromotionEnergySourceLiquidationCompensablePeriods,
    getPromotionEnergySourceLiquidationPendingCompensationPeriods,
    getPromotionEnergySourceLiquidationSummary,
    getPromotionEnergySourcesWithApplications,
    getPromotionEnergySourcesWithSupplyInvoices,
    getPromotionEnergySourcesWithLiquidatedPeriods,
    getPromotionEnergySourcesWithLiquidationPeriods,

    getPromotionsApplicationsPipesSensors,
    getPromotionApplicationPipeSensorReadings,

    getPromotionBlockHolder,
    getPromotionBlockPresident,
    getPromotionBlockAdministrator,
    getPromotionBlockFloorsWithApartments,

    getApplicationTypes,
    getMeasurementsUnits,
    getSupplyCompanies,
    getResidentialUsers,

    updatePromotionEnergySourceApplication,
    updatePromotionEnergySource,

    createPromotionEnergySupplyInvoice,
    updatePromotionEnergySupplyInvoice,
    updatePromotionEnergySupplyLiquidationPeriodicity,
    deletePromotionEnergySupplyInvoice,

    createEnergySourceLiquidatedPeriod,
    deleteEnergySourceLiquidatedPeriod,
    downloadEnergySourceLiquidatedPeriod,

    uploadPromotionImage,
    deletePromotionImage,

    uploadFile
  }
}